import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { MapperRegisterMemberMarketingService } from '../../../../core/mappers/cityparking/mapper-register-member-marketing.service';
import {
  RegisterMemberMarketingMonth,
  RegisterMemberMarketingResult,
  RegisterMemberTrendDirection,
} from '../../../../core/models/cityparking/register-member-marketing.model';
import { ApiRegisterMemberListService } from '../../../../core/services/api/cityparking/api-register-member-list.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import {
  buildReportExcelFilename,
  downloadReportExcelWorkbook,
} from '../../../../core/utils/report-excel-export.util';
import { exportElementToPdf } from '../../../../core/utils/capture-element-pdf.util';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';

type PeriodMode = 'year' | 'range';

@Component({
  selector: 'app-register-member-marketing',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoicePdfViewerComponent],
  templateUrl: './register-member-marketing.component.html',
  styleUrl: './register-member-marketing.component.css',
})
export class RegisterMemberMarketingComponent {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiRegisterMemberListService);
  private readonly mapper = inject(MapperRegisterMemberMarketingService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);

  isLoading = signal(false);
  hasSearched = signal(false);
  lastFetchedTime: Date | null = null;
  result = signal<RegisterMemberMarketingResult | null>(null);
  hoveredMonth = signal<number | null>(null);

  periodMode: PeriodMode = 'year';
  yearValue = '';
  startDate = '';
  endDate = '';
  userId = 0;

  private readonly maxRangeMonths = 12;
  yearOptions: number[] = [];

  constructor() {
    const now = new Date();
    this.yearValue = String(now.getFullYear());
    for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) {
      this.yearOptions.push(y);
    }
    const { start, end } = this.yearToRange(this.yearValue);
    this.startDate = start;
    this.endDate = end;

    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      this.siteState.selectedSite()?.id;
      untracked(() => this.clearResult());
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0 && this.userId > 0 && !!this.startDate && !!this.endDate;
  }

  get periodLabel(): string {
    if (this.periodMode === 'year') return `ปี ${this.yearValue}`;
    return `${this.formatDisplayDate(this.startDate)} – ${this.formatDisplayDate(this.endDate)}`;
  }

  get maxEndDate(): string | undefined {
    if (!this.startDate) return undefined;
    const start = this.parseInputDate(this.startDate);
    if (!start) return undefined;
    return this.toInputDate(
      new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate()),
    );
  }

  onPeriodModeChange(mode: PeriodMode): void {
    this.periodMode = mode;
    if (mode === 'year') {
      const { start, end } = this.yearToRange(this.yearValue);
      this.startDate = start;
      this.endDate = end;
    }
    this.clearResult();
  }

  onYearChange(): void {
    const { start, end } = this.yearToRange(this.yearValue);
    this.startDate = start;
    this.endDate = end;
    this.clearResult();
  }

  onFilterChange(): void {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
    if (this.startDate && this.endDate && this.isRangeOverMax(this.startDate, this.endDate)) {
      const maxEnd = this.maxEndDate;
      if (maxEnd) this.endDate = maxEnd;
      this.toast.warn('เรียกดูได้สูงสุด 12 เดือน — ปรับวันสิ้นสุดให้อัตโนมัติ');
    }
    this.clearResult();
  }

  onSearch(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.userId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงเวลา');
      return;
    }
    if (this.startDate > this.endDate) {
      this.toast.error('วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูได้สูงสุด 12 เดือนเท่านั้น');
      return;
    }
    this.loadDashboard();
  }

  onRefresh(): void {
    if (!this.hasSearched()) {
      this.onSearch();
      return;
    }
    this.loadDashboard();
  }

  loadDashboard(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.api
      .getRegisterMemberMarketingDashboard(
        this.selectedSiteId,
        this.userId,
        this.startDate,
        this.endDate,
      )
      .subscribe({
        next: (res) => {
          this.result.set(this.mapper.mapDashboard(res));
          this.hasSearched.set(true);
          this.lastFetchedTime = new Date();
          this.isLoading.set(false);
        },
        error: (err) => {
          this.result.set(null);
          this.hasSearched.set(true);
          this.isLoading.set(false);
          this.toast.error(err?.error?.message || 'โหลด Dashboard การตลาดไม่สำเร็จ');
        },
      });
  }

  goToRegisterMemberList(): void {
    void this.router.navigate(['/cityparking/register-member']);
  }

  formatDisplayDate(value: string | null | undefined): string {
    if (!value) return '—';
    const d = this.parseInputDate(value);
    if (!d) return value;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  }

  formatSigned(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toLocaleString()}`;
  }

  formatMomPercent(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value}%`;
  }

  trendIcon(direction: RegisterMemberTrendDirection | null | undefined): string {
    if (direction === 'up') return 'trending_up';
    if (direction === 'down') return 'trending_down';
    if (direction === 'pending') return 'hourglass_empty';
    return 'trending_flat';
  }

  trendToneClass(direction: RegisterMemberTrendDirection | null | undefined): string {
    if (direction === 'up') return 'trend--up';
    if (direction === 'down') return 'trend--down';
    if (direction === 'pending') return 'trend--pending';
    return 'trend--flat';
  }

  directionFromPercent(value: number | null): RegisterMemberTrendDirection {
    if (value === null) return 'flat';
    if (value > 0) return 'up';
    if (value < 0) return 'down';
    return 'flat';
  }

  monthCompareRows(series: RegisterMemberMarketingMonth[]): RegisterMemberMarketingMonth[] {
    return series;
  }

  hasIncompleteMonth(series: RegisterMemberMarketingMonth[]): boolean {
    return series.some((m) => m.isIncompleteMonth);
  }

  chartAxisTicks(max: number): { label: string }[] {
    const safeMax = Math.max(1, max);
    return [1, 0.75, 0.5, 0.25, 0].map((ratio) => ({
      label: Math.round(safeMax * ratio).toLocaleString('th-TH'),
    }));
  }

  private clearResult(): void {
    this.result.set(null);
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
    this.hoveredMonth.set(null);
  }

  private yearToRange(yearStr: string): { start: string; end: string } {
    const y = Number(yearStr) || new Date().getFullYear();
    return {
      start: `${y}-01-01`,
      end: `${y}-12-31`,
    };
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseInputDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isRangeOverMax(startStr: string, endStr: string): boolean {
    const start = this.parseInputDate(startStr);
    const end = this.parseInputDate(endStr);
    if (!start || !end) return false;
    const maxEnd = new Date(
      start.getFullYear(),
      start.getMonth() + this.maxRangeMonths,
      start.getDate(),
    );
    return end > maxEnd;
  }

  isExporting = signal(false);
  isProcessingPdf = signal(false);
  showPdfModal = signal(false);
  pdfSrc: Uint8Array | null = null;
  pdfDownloadName = 'register-member-marketing.pdf';

  async onExportExcel(): Promise<void> {
    const data = this.result();
    if (!data || this.isExporting()) return;

    this.isExporting.set(true);
    try {
      const s = data.summary;
      const t = data.trendInsight;
      const topType = data.vehicleTypeBreakdown[0];
      const hasIncomplete = this.hasIncompleteMonth(data.monthlySeries);
      const summaryRow = (group: string, title: string, value: string | number, detail = '') =>
        ({ 'กลุ่ม': group, 'หัวข้อ': title, 'ค่า': value, 'รายละเอียด': detail });

      const summaryRows: Record<string, string | number>[] = [
        ...data.kpiCards.map((k) => summaryRow('KPI', k.title, k.value, k.hint ?? '')),
        summaryRow('แนวโน้ม', 'ทิศทางแนวโน้มยอดชำระ', t.label,
          t.periodChangePercent !== null
            ? `${t.firstLabel} → ${t.lastLabel} · ${this.formatMomPercent(t.periodChangePercent)} (${t.firstTotal.toLocaleString()} → ${t.lastTotal.toLocaleString()})`
            : ''),
        summaryRow('แนวโน้ม', 'เดือนยอดชำระสูงสุด', s.peakMonthLabel || '—', `${s.peakMonthTotal.toLocaleString()} ราย`),
        summaryRow('แนวโน้ม', 'เดือนยอดชำระต่ำสุด', s.lowestMonthLabel || '—', `${s.lowestMonthTotal.toLocaleString()} ราย`),
        summaryRow('แนวโน้ม', 'MoM เดือนล่าสุด (จบแล้ว)', this.formatMomPercent(s.momChangePercent),
          hasIncomplete ? 'ไม่นับเดือนปัจจุบันที่ยังไม่จบ' : ''),
        ...(data.monthlySeries.length >= 2
          ? [
              summaryRow('แนวโน้ม', 'เดือนที่ขึ้น', t.risingMonths),
              summaryRow('แนวโน้ม', 'เดือนที่ลง', t.fallingMonths),
              summaryRow('แนวโน้ม', 'MoM เฉลี่ย', this.formatMomPercent(t.avgMomPercent)),
            ]
          : []),
        summaryRow('สถานะคำขอ', 'คำขอสมัครทั้งหมด', s.totalCount),
        summaryRow('สถานะคำขอ', 'ชำระแล้ว', s.paidCount, `Conversion ${s.conversionPercent}%`),
        summaryRow('สถานะคำขอ', 'รอชำระ', s.unpaidCount),
        summaryRow('สถานะคำขอ', 'หมดอายุไม่ชำระ', s.expiredUnpaidCount),
        summaryRow('สถานะคำขอ', 'ยกเลิก', s.cancelledCount),
        ...(topType
          ? [summaryRow('ประเภทรถ', 'ประเภทสูงสุด', topType.vehicleTypeName, `${topType.count.toLocaleString()} ราย · ${topType.percent}%`)]
          : []),
      ];

      const monthlyRows: Record<string, string | number>[] = data.monthlySeries.map((m) => ({
        'เดือน': m.label,
        'ชำระ': m.paid,
        'เดือนก่อน': m.prevPaid ?? '—',
        'ผลต่าง': m.isIncompleteMonth || m.momDelta === null ? '—' : this.formatSigned(m.momDelta),
        '% เปลี่ยน': m.isIncompleteMonth
          ? 'ยังไม่จบ'
          : (m.momChangePercent === null ? '—' : this.formatMomPercent(m.momChangePercent)),
        'แยกประเภทรถ': m.segments.length > 0
          ? m.segments.map((seg) => `${seg.vehicleTypeName} ${seg.count.toLocaleString()} (${seg.pct}%)`).join(', ')
          : '—',
        'ทิศทาง': this.trendText(m),
        'หมายเหตุ': this.monthRemark(m),
      }));
      if (monthlyRows.length > 0) {
        monthlyRows.push({
          'เดือน': 'รวม',
          'ชำระ': data.monthlySeries.reduce((sum, m) => sum + m.paid, 0),
          'เดือนก่อน': '',
          'ผลต่าง': '',
          '% เปลี่ยน': '',
          'แยกประเภทรถ': '',
          'ทิศทาง': '',
          'หมายเหตุ': '',
        });
      }

      const vehicleRows: Record<string, string | number>[] = data.vehicleTypeBreakdown.map((v) => ({
        'ประเภทรถ': v.vehicleTypeName,
        'ยอดชำระ': v.count,
        'สัดส่วน (%)': v.percent,
      }));

      const typeColumns = this.uniqueVehicleTypeColumns(data.vehicleTypeMonthlySeries.map((v) => v.vehicleTypeName));
      const matrixRows: Record<string, string | number>[] = data.monthlySeries.map((m, monthIndex) => {
        const row: Record<string, string | number> = { 'เดือน': m.label };
        data.vehicleTypeMonthlySeries.forEach((series, i) => {
          row[typeColumns[i]] = series.points[monthIndex]?.total ?? 0;
        });
        row['รวม'] = m.paid;
        return row;
      });

      const siteName = data.siteName || this.siteState.selectedSite()?.name || '—';
      await downloadReportExcelWorkbook(
        [
          { sheetName: 'สรุป', dataRows: summaryRows },
          { sheetName: 'รายเดือน (MoM)', dataRows: monthlyRows },
          { sheetName: 'ประเภทรถ', dataRows: vehicleRows },
          { sheetName: 'เดือน x ประเภทรถ', dataRows: matrixRows },
        ],
        {
          reportTitle: 'รายงานการตลาด — สมัครสมาชิก (ยอดชำระ)',
          siteName,
          siteAddress: data.siteAddress,
          siteTaxId: data.siteTaxId,
          startDate: this.startDate,
          endDate: this.endDate,
          extraLines: this.lastFetchedTime
            ? [`ดึงข้อมูลล่าสุด: ${this.formatDateTime(this.lastFetchedTime.toISOString())}`]
            : [],
          fileName: buildReportExcelFilename('REGISTER_MEMBER_MARKETING', siteName, this.startDate, this.endDate),
        },
      );
      this.toast.success('ส่งออก Excel สำเร็จ');
    } catch {
      this.toast.error('ส่งออก Excel ไม่สำเร็จ');
    } finally {
      this.isExporting.set(false);
    }
  }

  onPrintPdf(): void {
    if (!this.result() || !this.canSearch || this.isProcessingPdf()) return;

    this.isProcessingPdf.set(true);
    const siteName = (this.result()?.siteName || this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
    this.pdfDownloadName = `REGISTER_MEMBER_MARKETING_${siteName}_${this.startDate}_${this.endDate}.pdf`;

    this.api
      .getRegisterMemberMarketingPdf({
        siteId: this.selectedSiteId,
        startDate: this.startDate,
        endDate: this.endDate,
      })
      .subscribe({
        next: (bytes) => {
          this.pdfSrc = bytes;
          this.showPdfModal.set(true);
          this.isProcessingPdf.set(false);
        },
        error: (err) => {
          console.error('[API Error] getRegisterMemberMarketingPdf:', err);
          this.toast.error('ไม่สามารถดึงไฟล์ PDF ได้');
          this.isProcessingPdf.set(false);
        },
      });
  }

  closePdfModal(): void {
    this.showPdfModal.set(false);
    this.pdfSrc = null;
  }

  private monthRemark(m: RegisterMemberMarketingMonth): string {
    const notes: string[] = [];
    if (m.isPeak) notes.push('สูงสุด');
    if (m.isLowest) notes.push('ต่ำสุด');
    if (m.isIncompleteMonth) notes.push('เดือนปัจจุบันยังไม่จบ');
    return notes.join(', ');
  }

  private trendText(m: RegisterMemberMarketingMonth): string {
    if (m.isIncompleteMonth) return 'ยังไม่จบ';
    if (m.momDelta === null) return '—';
    if (m.trend === 'up') return 'ขึ้น';
    if (m.trend === 'down') return 'ลง';
    return 'คงที่';
  }

  /** ชื่อคอลัมน์ Excel ต้องไม่ซ้ำ และไม่ชนกับ "เดือน" / "รวม" */
  private uniqueVehicleTypeColumns(names: string[]): string[] {
    const used = new Set<string>(['เดือน', 'รวม']);
    return names.map((name) => {
      const base = (name || 'ไม่ระบุประเภท').trim();
      let candidate = base;
      let n = 2;
      while (used.has(candidate)) candidate = `${base} (${n++})`;
      used.add(candidate);
      return candidate;
    });
  }

  @ViewChild('captureArea') captureArea?: ElementRef<HTMLElement>;
  isCapturing = signal(false);

  async onCapturePdf(): Promise<void> {
    const data = this.result();
    if (!data || this.isCapturing()) return;

    this.hoveredMonth.set(null);
    this.isCapturing.set(true);
    try {
      // รอให้หัวรายงาน (แสดงเฉพาะตอนแคป) render ก่อนวัดขนาด
      await new Promise((resolve) => setTimeout(resolve, 80));
      const element = this.captureArea?.nativeElement;
      if (!element) throw new Error('capture area not found');

      const siteName = (data.siteName || this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
      await exportElementToPdf(element, {
        fileName: `REGISTER_MEMBER_MARKETING_SCREEN_${siteName}_${this.startDate}_${this.endDate}.pdf`,
        orientation: 'landscape',
        footerText: `Printed ${this.formatDateTime(new Date().toISOString())}`,
        onClone: (root, doc) => this.replaceDonutForCapture(root, doc, data),
      });
      this.toast.success('บันทึกหน้าจอเป็น PDF สำเร็จ');
    } catch (err) {
      console.error('[Capture PDF] register-member-marketing:', err);
      this.toast.error('แคปหน้าจอเป็น PDF ไม่สำเร็จ');
    } finally {
      this.isCapturing.set(false);
    }
  }

  /** html2canvas วาด conic-gradient ไม่ได้ — แทนวงโดนัทด้วย SVG ใน DOM ที่ clone */
  private replaceDonutForCapture(root: HTMLElement, doc: Document, data: RegisterMemberMarketingResult): void {
    const donut = root.querySelector<HTMLElement>('.donut');
    if (!donut) return;

    const r = 40;
    const circumference = 2 * Math.PI * r;
    const ring = (color: string, startPct: number, endPct: number) => {
      const len = Math.max(0, (endPct - startPct) / 100) * circumference;
      const offset = -(startPct / 100) * circumference;
      return `<circle cx="50" cy="50" r="${r}" fill="none" stroke="${color}" stroke-width="20"
        stroke-dasharray="${len} ${circumference - len}" stroke-dashoffset="${offset}" transform="rotate(-90 50 50)"/>`;
    };
    const rings = data.summary.paidCount > 0 && data.vehicleTypeBreakdown.length > 0
      ? data.vehicleTypeBreakdown.map((v) => ring(v.color, v.donutStartPct, v.donutEndPct)).join('')
      : ring('#e2e8f0', 0, 100);

    const holder = doc.createElement('div');
    holder.style.cssText = 'position:absolute;inset:0;';
    holder.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100%" height="100%">${rings}</svg>`;

    donut.style.setProperty('background', 'transparent', 'important');
    donut.style.position = 'relative';
    donut.insertBefore(holder, donut.firstChild);
    const hole = donut.querySelector<HTMLElement>('.donut__hole');
    if (hole) {
      hole.style.position = 'relative';
      hole.style.zIndex = '1';
    }
  }
}
